import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useSSE } from "@/hooks/useSSE";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLLING_INTERVAL = 60_000;

const NotifikationerContext = createContext(null);

export function NotifikationerProvider({ children }) {
  const { token } = useAuth();
  const { lastEvent } = useSSE(token);

  const [ulæstBeskeder, setUlæstBeskeder] = useState(0);
  const [ulæstNotifikationer, setUlæstNotifikationer] = useState(0);
  const [sisteEvent, setSisteEvent] = useState(null);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);

  const fetchUlæstAntal = async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/notifikationer/ulaest-antal`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (typeof data.antal === "number") setUlæstNotifikationer(data.antal);
    } catch {
      // Stille fejl — polling prøver igen
    }
  };

  const fetchUlæstBeskeder = async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/chat/kanaler`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const total = (data || []).reduce((sum, k) => sum + (k.ulaest_antal || 0), 0);
      setUlæstBeskeder(total);
    } catch {
      // Stille fejl
    }
  };

  useEffect(() => {
    fetchUlæstAntal();
    fetchUlæstBeskeder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const id = setInterval(() => {
      fetchUlæstAntal();
      fetchUlæstBeskeder();
    }, POLLING_INTERVAL);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const prevEventRef = useRef(null);
  useEffect(() => {
    if (!lastEvent || lastEvent === prevEventRef.current) return;
    prevEventRef.current = lastEvent;
    setSisteEvent(lastEvent);

    if (lastEvent.type === "besked") {
      setUlæstBeskeder((n) => n + 1);
      const navn = lastEvent.melding?.afsender_navn || "nogen";
      toast(`Ny besked fra ${navn}`);
    } else if (lastEvent.type === "notifikation") {
      setUlæstNotifikationer((n) => n + 1);
      toast(lastEvent.titel || "Ny notifikation");
    }
  }, [lastEvent]);

  return (
    <NotifikationerContext.Provider
      value={{ ulæstBeskeder, ulæstNotifikationer, sisteEvent, setUlæstBeskeder, setUlæstNotifikationer, chatPanelOpen, setChatPanelOpen }}
    >
      {children}
    </NotifikationerContext.Provider>
  );
}

export function useNotifikationer() {
  const ctx = useContext(NotifikationerContext);
  if (!ctx) throw new Error("useNotifikationer skal bruges inden i NotifikationerProvider");
  return ctx;
}
